import { inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";

import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { GlobalService } from "@bitwarden/common/services/global/global.service";
import {
  AcknowledgeAutoRedemptionResponse,
  AttemptAutoRedemptionResponse,
  AutoRedemptionCronAllResponse,
  AutoRedemptionCronUserResponse,
  AUTO_REDEEM_CREDITS,
  AutoRedemptionEnrollment,
  GetActiveWithPendingResponse,
  PendingAutoRedemption,
} from "@bitwarden/web-vault/app/models/types/auto-credit-redemption.types";
import {
  CreditConfigType,
  SubscriptionConfigType,
} from "@bitwarden/web-vault/app/models/types/environement-config.type";
import { capFetch } from "@bitwarden/web-vault/utils/fetch";
import { StateService } from "@bitwarden/web-vault/app/core";
import { PreferenceView } from "@bitwarden/web-vault/app/models/view/preference/preference.view";
import { SubscriptionView } from "@bitwarden/web-vault/app/models/view/subscription/subscription.view";
import { DataRepositoryService } from "@bitwarden/web-vault/app/services/DataRepository/data-repository.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { CreditsService } from "@bitwarden/web-vault/app/services/syncing/gloss-credits/credits.service";
import { PerformanceService } from "@bitwarden/web-vault/app/services/performance/performance.service";
import { GlossRoutes } from "@bitwarden/web-vault/app/models/enum/routes.enum";
import { HelperTrialRemaining } from "@bitwarden/web-vault/app/shared/utils/helper-trial-remaining";
import { autoCreditRedemptionEnabled } from "@bitwarden/web-vault/utils/flags";

import {
  canShowAutoRedeem,
  daysUntilRenewal,
  isWithinRedemptionWindow,
  isUserNotifiableOutcome,
  nextRenewalReminderDay,
  resolveSubscriptionResponseFromGetActive,
  toastKeyForAutoRedemptionOutcome,
  toAutoRedeemRef,
} from "./auto-credit-redemption.eligibility";

const SUBSCRIPTION_CONFIG = process.env.SUBSCRIPTION as unknown as SubscriptionConfigType;
const CREDIT_CONFIG = process.env.CREDIT as unknown as CreditConfigType;

/**
 * Per-user, per-subscription record of the lowest renewal reminder already shown
 * (localStorage, per device). The user id keeps two accounts in one browser apart.
 */
const RENEWAL_REMINDER_STORAGE_PREFIX = "autoRedemptionRenewalReminder:";
const RENEWAL_REMINDER_TOAST_TIMEOUT_MS = 10000;

type RenewalReminderRecord = { renewal: string; lowest: number };

@Injectable({
  providedIn: "root",
})
export class AutoCreditRedemptionService {
  private dataRepository = inject(DataRepositoryService);
  private userStore = inject(UserStoreService);
  private stateService = inject(StateService);
  private creditsService = inject(CreditsService);
  private globalService = inject(GlobalService);
  private i18nService = inject(I18nService);
  private router = inject(Router);
  private trialHelper = new HelperTrialRemaining();
  private log = inject(LogService);
  private performance = inject(PerformanceService);
  private postLoginHandled = false;

  isFeatureEnabled(): boolean {
    // Build flag only: available to every user. The toggle and every enrollment/redemption
    // path key off this, so the flag stays as a kill switch.
    return autoCreditRedemptionEnabled();
  }

  canShowToggle(active: SubscriptionView | null = this.activeSubscription()): boolean {
    return this.isFeatureEnabled() && canShowAutoRedeem(toAutoRedeemRef(active));
  }

  isPreferenceEnabled(): boolean {
    const pref = this.userStore.preferences.preferenceView();
    if (!pref) {
      return true;
    }
    return pref.autoCreditRedemption !== false;
  }

  /** True after `handlePostLogin` completes its sync/enrollment pass this session. */
  hasHandledPostLogin(): boolean {
    return this.postLoginHandled;
  }

  /** Post-login: show pending outcome toasts, then optional bootstrap attempt. */
  async handlePostLogin(): Promise<void> {
    this.postLoginHandled = false;

    if (!this.isFeatureEnabled()) {
      return;
    }

    try {
      const activeResponse = await this.fetchActiveWithPending();
      await this.syncActiveSubscriptionFromGlossApi(activeResponse);
      await this.ensureEnrollmentIfOptedIn();

      const pendingList = activeResponse?.pending_auto_redemption
        ? [activeResponse.pending_auto_redemption]
        : [];

      for (const outcome of pendingList) {
        if (!isUserNotifiableOutcome(outcome)) {
          continue;
        }
        await this.showAndAcknowledgeOutcome(outcome);
      }

      const attempt = await this.runBootstrapRedemptionIfEligible("login", activeResponse);

      this.postLoginHandled = true;

      // Any outcome (just shown, or recorded earlier: already_processed) settles or explains this
      // renewal; a "top up" reminder on top would double up or contradict it. The 1-day reminder
      // sits inside the 48h redemption window, so by then the renewal has usually been decided.
      if (!pendingList.length && !attempt?.attempted && attempt?.reason !== "already_processed") {
        await this.showRenewalReminderIfNeeded();
      }
    } catch (error) {
      this.recordApm("post_login_error", {
        error: error instanceof Error ? error.message : String(error),
      });
      this.log.warning(
        `Auto credit redemption post-login skipped: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  /**
   * Client bootstrap: POST gloss-credit `/attempt` when local guards pass.
   * Called from `handlePostLogin` only (post-login backup when cron has not run).
   */
  async runBootstrapRedemptionIfEligible(
    source: "login" | "test-panel",
    activeResponse?: GetActiveWithPendingResponse | null,
  ): Promise<AttemptAutoRedemptionResponse | null> {
    if (!this.isFeatureEnabled()) {
      return null;
    }

    try {
      const resolved = activeResponse ?? (await this.fetchActiveWithPending());
      if (activeResponse === undefined) {
        await this.syncActiveSubscriptionFromGlossApi(resolved);
      }

      if (this.creditsService.accounts().length === 0) {
        await this.creditsService.fetch();
      } else {
        await this.creditsService.ensureGlossApiUserLinked();
      }

      const active = this.activeSubscription();
      const renewalDate =
        resolved?.date_renewal ??
        (active?.renewalDate && !Number.isNaN(active.renewalDate.getTime())
          ? active.renewalDate.toISOString()
          : undefined);
      const accountId = this.creditAccountId();

      if (!this.isPreferenceEnabled()) {
        this.logBootstrapSkip(source, "preference_opted_out");
        return null;
      }
      if (!canShowAutoRedeem(toAutoRedeemRef(active))) {
        this.logBootstrapSkip(source, "subscription_not_eligible");
        return null;
      }
      if (!isWithinRedemptionWindow(renewalDate)) {
        this.logBootstrapSkip(source, `outside_redemption_window (${renewalDate ?? "missing"})`);
        return null;
      }
      if (!accountId) {
        this.logBootstrapSkip(source, "credit_account_missing");
        return null;
      }

      const attempt = await this.attemptAutoRedemption({
        accountId,
        amount: AUTO_REDEEM_CREDITS,
      });

      if (!attempt.attempted && attempt.reason) {
        this.log.warning(`[auto-redeem ${source}] attempt skipped: ${attempt.reason}`);
      }

      this.recordApm("bootstrap_attempt", {
        source,
        attempted: attempt.attempted,
        reason: attempt.reason ?? "",
        outcome_status: attempt.outcome?.status ?? "",
      });

      if (attempt.outcome && isUserNotifiableOutcome(attempt.outcome)) {
        await this.showAndAcknowledgeOutcome(attempt.outcome);
      }

      return attempt;
    } catch (error) {
      this.recordApm("bootstrap_error", {
        source,
        error: error instanceof Error ? error.message : String(error),
      });
      this.log.warning(
        `[auto-redeem ${source}] bootstrap failed: ${error instanceof Error ? error.message : String(error)}`,
      );
      return null;
    }
  }

  private logBootstrapSkip(source: string, reason: string): void {
    this.recordApm("bootstrap_skip", { source, reason });
    this.log.warning(`[auto-redeem ${source}] bootstrap skipped: ${reason}`);
  }

  /**
   * Warns 7/5/3/1 days before renewal when the credit balance can't cover the auto-redemption,
   * so the user can top up before the redemption window opens (#5490). Each threshold fires
   * once per renewal period per user and device. Same gates as the toggle: feature flag +
   * eligible plan + opted in, and not during the free trial (the free-trial reminder already covers the
   * first charge). Runs after the getActive sync, so `renewalDate` is fresh rather than the login
   * JWT's.
   */
  async showRenewalReminderIfNeeded(): Promise<boolean> {
    try {
      const active = this.activeSubscription();
      if (!this.canShowToggle(active) || !this.isPreferenceEnabled()) {
        return false;
      }

      // The free-trial reminder already covers the first charge; a second toast would double up.
      if (this.trialHelper.isInTrialPeriod(active)) {
        return false;
      }

      const daysLeft = daysUntilRenewal(active.renewalDate);
      if (daysLeft === null) {
        return false;
      }

      const userId = (await this.stateService.getUserId())?.trim();
      if (!userId) {
        // Nothing to key the shown-history on - skip rather than warn again on every login.
        return false;
      }

      const renewal = active.renewalDate.toISOString();
      const storageKey = `${RENEWAL_REMINDER_STORAGE_PREFIX}${userId}:${active.id}`;
      const reminderDay = nextRenewalReminderDay(
        daysLeft,
        this.readLowestReminderShown(storageKey, renewal),
      );
      if (reminderDay === null) {
        return false;
      }

      // Credits were loaded by the bootstrap attempt just before; empty means unknown balance.
      if (this.creditsService.accounts().length === 0) {
        // Don't warn on a guess.
        return false;
      }

      const balance = this.creditsService.totalGoldCredits();
      if (balance >= AUTO_REDEEM_CREDITS) {
        // Not recorded as shown: the balance can drop before the next threshold.
        return false;
      }

      this.showRenewalReminder(daysLeft, active.renewalDate, balance);
      this.writeLowestReminderShown(storageKey, { renewal, lowest: reminderDay });
      this.recordApm("renewal_reminder", {
        days_left: daysLeft,
        threshold: reminderDay,
        credits_available: balance,
      });
      return true;
    } catch (error) {
      this.recordApm("renewal_reminder_error", {
        error: error instanceof Error ? error.message : String(error),
      });
      this.log.warning(
        `Renewal credit reminder skipped: ${error instanceof Error ? error.message : String(error)}`,
      );
      return false;
    }
  }

  private showRenewalReminder(daysLeft: number, renewalDate: Date, balance: number): void {
    // Same yyyy-MM-dd the Subscription page shows for the renewal date. Two sentences composed
    // (like the free-trial reminder) because I18nService.t takes at most three parameters.
    const date = this.trialHelper.formatBillingDate(renewalDate);
    const renewsIn =
      daysLeft === 1
        ? this.i18nService.t("autoCreditRedemptionRenewsTomorrow", date)
        : this.i18nService.t("autoCreditRedemptionRenewsInDays", daysLeft.toString(), date);
    const shortfall = this.i18nService.t(
      "autoCreditRedemptionCreditShortfall",
      Math.floor(balance).toLocaleString("en-US"),
      AUTO_REDEEM_CREDITS.toLocaleString("en-US"),
    );

    this.globalService.showMessage(
      "warning",
      this.i18nService.t("autoCreditRedemptionTitle"),
      `${renewsIn} ${shortfall}`,
      false,
      {
        timeout: RENEWAL_REMINDER_TOAST_TIMEOUT_MS,
        payload: {
          actionLabel: this.i18nService.t("getCredits"),
          onAction: () => void this.router.navigate(["/", GlossRoutes.Credits]),
        },
      },
    );
  }

  private readLowestReminderShown(storageKey: string, renewal: string): number | null {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        return null;
      }
      const record = JSON.parse(raw) as RenewalReminderRecord;
      return record.renewal === renewal && Number.isFinite(record.lowest) ? record.lowest : null;
    } catch {
      return null;
    }
  }

  private writeLowestReminderShown(storageKey: string, record: RenewalReminderRecord): void {
    try {
      localStorage.setItem(storageKey, JSON.stringify(record));
    } catch {
      // Storage unavailable - worst case the reminder repeats on the next login.
    }
  }

  private recordApm(event: string, labels: Record<string, string | number | boolean> = {}): void {
    this.performance.recordAutoRedemptionEvent({
      acr_event: event,
      ...labels,
    });
  }

  /**
   * When opted in (default ON), ensure gloss-credit enrollment matches the vault preference.
   * Runs after login and when the auto-redeem toggle is mounted (Bundle / Billing pages).
   */
  async ensureEnrollmentIfOptedIn(): Promise<void> {
    if (!this.isFeatureEnabled()) {
      return;
    }
    if (!this.canShowToggle()) {
      return;
    }
    if (!this.isPreferenceEnabled()) {
      return;
    }

    try {
      const enrollment = await this.fetchEnrollment();
      if (enrollment.enabled) {
        return;
      }
    } catch {
      // Continue — enrollment may not exist yet.
    }

    const pref = this.userStore.preferences.preferenceView()?.clone();
    if (!pref) {
      return;
    }

    try {
      await this.syncEnrollmentFromPreference(true, pref);
    } catch (error) {
      this.log.warning(
        `Auto credit redemption enrollment sync skipped: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  /** Sync gloss-credit enrollment when the user toggles the vault preference. */
  async syncEnrollmentFromPreference(enabled: boolean, preference?: PreferenceView): Promise<void> {
    if (!this.isFeatureEnabled()) {
      return;
    }

    if (!enabled) {
      await this.setEnrollment({ enabled: false });
      this.recordApm("enrollment_sync", { enabled: false });
      if (preference) {
        await this.userStore.preferences.save(preference);
      }
      return;
    }

    const active = this.activeSubscription();
    if (!canShowAutoRedeem(toAutoRedeemRef(active))) {
      throw new Error("Auto credit redemption is not available for this subscription");
    }

    if (this.creditsService.accounts().length === 0) {
      await this.creditsService.fetch();
    }

    const accountId = this.creditAccountId();
    if (!accountId || !this.creditsService.glossBankingApiKey()) {
      throw new Error("Gloss Credits account or API key is not available");
    }

    await this.setEnrollment({
      enabled: true,
      accountId,
      amount: AUTO_REDEEM_CREDITS,
    });
    this.recordApm("enrollment_sync", { enabled: true });

    if (preference) {
      await this.userStore.preferences.save(preference);
    }
  }

  async fetchEnrollment(): Promise<AutoRedemptionEnrollment> {
    return this.creditRequest<AutoRedemptionEnrollment>(
      "GET",
      CREDIT_CONFIG.endpoint.auto_redemption_enrollment,
    );
  }

  async setEnrollment(
    body: { enabled: false } | { enabled: true; accountId: string; amount: number },
  ): Promise<AutoRedemptionEnrollment> {
    const payload =
      body.enabled === false
        ? { enabled: false }
        : {
            enabled: true,
            accountId: body.accountId,
            amount: body.amount,
          };

    return this.creditRequest<AutoRedemptionEnrollment>(
      "PUT",
      CREDIT_CONFIG.endpoint.auto_redemption_enrollment,
      payload,
    );
  }

  async attemptAutoRedemption(body: {
    accountId: string;
    amount: number;
  }): Promise<AttemptAutoRedemptionResponse> {
    return this.creditRequest<AttemptAutoRedemptionResponse>(
      "POST",
      CREDIT_CONFIG.endpoint.auto_redemption_attempt,
      {
        accountId: body.accountId,
        amount: body.amount,
      },
    );
  }

  /**
   * Run daily cron logic for the logged-in gloss-credit user (`trigger: cron`).
   * @param options.silent — no toasts, credits refresh, or subscription sync (background cron; test login UX separately).
   */
  async runCronForCurrentUser(options?: {
    silent?: boolean;
  }): Promise<AutoRedemptionCronUserResponse> {
    const result = await this.creditRequest<AutoRedemptionCronUserResponse>(
      "POST",
      CREDIT_CONFIG.endpoint.auto_redemption_cron,
    );
    if (!options?.silent) {
      await this.applyCronUserResult(result);
    }
    this.recordApm("cron_user", {
      silent: options?.silent === true,
      processed: result.processed,
      attempted: result.attempted,
      reason: result.reason ?? "",
      outcome_status: result.outcome?.status ?? "",
    });
    return result;
  }

  /** Run cron for every enrolled user (local/dev only on gloss-credit). */
  async runCronForAllEnrolled(): Promise<AutoRedemptionCronAllResponse> {
    return this.creditRequest<AutoRedemptionCronAllResponse>(
      "POST",
      CREDIT_CONFIG.endpoint.auto_redemption_cron_all,
    );
  }

  private async applyCronUserResult(result: AutoRedemptionCronUserResponse): Promise<void> {
    if (result.outcome?.status === "success") {
      await this.refreshCreditsAfterAutoRedemption();
      await this.syncActiveSubscriptionFromGlossApi();
    }
    if (result.outcome && isUserNotifiableOutcome(result.outcome)) {
      await this.showAndAcknowledgeOutcome(result.outcome);
    }
  }

  async fetchActiveWithPending(): Promise<GetActiveWithPendingResponse | null> {
    try {
      return await this.subscriptionRequest<GetActiveWithPendingResponse>(
        "GET",
        SUBSCRIPTION_CONFIG.endpoint.get_active_subscription,
      );
    } catch (error) {
      this.log.warning(
        `getActive with pending auto-redemption failed: ${error instanceof Error ? error.message : String(error)}`,
      );
      return null;
    }
  }

  /**
   * Refresh in-memory subscription from gloss-api (CLI/Dynamo updates are not in the login JWT).
   */
  async syncActiveSubscriptionFromGlossApi(
    active?: GetActiveWithPendingResponse | null,
  ): Promise<void> {
    const resolved = active ?? (await this.fetchActiveWithPending());
    if (!resolved) {
      return;
    }

    const { pending_auto_redemption: _pending, ...apiPayload } = resolved;
    const subscription = resolveSubscriptionResponseFromGetActive(
      apiPayload,
      this.activeSubscription(),
    );

    if (!subscription) {
      return;
    }

    this.userStore.subscription.applyFromGlossApi(subscription);
  }

  async acknowledgeOutcome(outcomeId: string): Promise<AcknowledgeAutoRedemptionResponse> {
    const path = `${SUBSCRIPTION_CONFIG.endpoint.auto_redemption_acknowledge}/${outcomeId}/acknowledge`;
    return this.subscriptionRequest<AcknowledgeAutoRedemptionResponse>("POST", path);
  }

  toastKeyForOutcome(outcome: PendingAutoRedemption): string {
    return toastKeyForAutoRedemptionOutcome(outcome);
  }

  private async refreshCreditsAfterAutoRedemption(): Promise<void> {
    try {
      await this.creditsService.fetch();
    } catch (error) {
      this.log.warning(
        `Failed to refresh credits after auto-redemption: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  private async showAndAcknowledgeOutcome(outcome: PendingAutoRedemption): Promise<void> {
    this.recordApm("outcome_toast", {
      outcome_status: outcome.status,
      skip_reason: outcome.skip_reason ?? "",
      renewal_period: outcome.renewal_period ?? "",
    });

    if (outcome.status === "success") {
      await this.refreshCreditsAfterAutoRedemption();
      await this.syncActiveSubscriptionFromGlossApi();
    }

    const messageKey = this.toastKeyForOutcome(outcome);
    const toastType =
      outcome.status === "success"
        ? "success"
        : outcome.status === "insufficient_credits"
          ? "warning"
          : outcome.status === "skipped"
            ? "info"
            : "error";

    this.globalService.showMessage(toastType, "autoCreditRedemptionTitle", messageKey);

    if (!outcome.id) {
      this.log.warning(
        `[auto-redeem] skip acknowledge — outcome.id missing (status=${outcome.status}, renewal_period=${outcome.renewal_period})`,
      );
      return;
    }

    try {
      await this.acknowledgeOutcome(outcome.id);
    } catch (error) {
      this.log.warning(
        `Failed to acknowledge auto-redemption outcome ${outcome.id}: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  private activeSubscription(): SubscriptionView | null {
    return this.userStore.subscription.activeSubscription();
  }

  private creditAccountId(): string | null {
    return this.creditsService.accounts()[0]?._id ?? null;
  }

  private subscriptionApiBase(): string {
    const stage = SUBSCRIPTION_CONFIG.apiStage;
    if (stage) {
      return `${SUBSCRIPTION_CONFIG.url}/${stage}`;
    }
    return SUBSCRIPTION_CONFIG.url;
  }

  private subscriptionRequest<T>(
    method: "GET" | "PUT" | "POST",
    path: string,
    body: unknown = null,
  ): Promise<T> {
    return this.dataRepository.send(method, path, body, true, true, this.subscriptionApiBase());
  }

  private creditApiBase(): string {
    return this.creditsService.baseUrl;
  }

  private async creditRequest<T>(
    method: "GET" | "PUT" | "POST",
    path: string,
    body?: unknown,
  ): Promise<T> {
    const apiKey = this.creditsService.glossBankingApiKey();
    if (!apiKey) {
      throw new Error("Credit API key is not available");
    }

    const hasBody = body !== null && body !== undefined;

    const response = await capFetch<T>({
      url: `${this.creditApiBase()}${path}`,
      method,
      headers: {
        Authorization: `Bearer ${apiKey}`,
        ...(hasBody ? { "Content-Type": "application/json" } : {}),
      },
      ...(hasBody ? { data: body } : {}),
    });

    if (response.status < 200 || response.status > 299) {
      const msg =
        response.data && typeof response.data === "object" && "message" in response.data
          ? String((response.data as { message?: string }).message)
          : `HTTP ${response.status}`;
      throw new Error(`Credit API request failed: ${msg}`);
    }

    return response.data as T;
  }
}
